/**
 * ProtectedRoute Component
 *
 * Wraps routes that require authentication.
 * - During session recovery: Shows loading spinner
 * - If no currentUser: Redirects to /login
 * - If authenticated: Renders children
 *
 * Usage:
 * ```tsx
 * <Route path="/" element={
 *   <ProtectedRoute>
 *     <DashboardPage />
 *   </ProtectedRoute>
 * } />
 * ```
 */

import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useVendorAuth } from "../../features/auth/application/hooks/useVendorAuth";
import { LogoLoader } from "@semo/shared-ui";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { currentUser, isLoading, isSessionLoading } = useVendorAuth();
  const location = useLocation();

  // 저장된 토큰을 읽는 중(isLoading)에도 기다린다. 세션 확인 중만 기다리면 첫 순간 사용자가
  // 비어 /login 으로 보냈고, 주소로 바로 연 화면이 첫 화면으로 바뀌었다(A3-28).
  if (isLoading || isSessionLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LogoLoader size="md" text="세션 확인 중..." showText />
      </div>
    );
  }

  if (!currentUser) {
    // 로그인 뒤 원래 보려던 화면으로 돌아오게 주소를 넘긴다.
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: `${location.pathname}${location.search}` }}
      />
    );
  }

  return <>{children}</>;
}
